<template>
  <aside class="vtta">
    <div class="vtta__toolbar">
      <el-input
        v-model="searchText"
        :placeholder="searchPlaceholder"
        clearable
        prefix-icon="el-icon-search"
        class="vtta__search"
        @input="onSearchInput"
        @clear="onSearchInput"
      />
      <button
        type="button"
        class="vtta__icon-btn"
        title="刷新"
        :disabled="treeLoading"
        @click="loadTeamTree"
      >
        <i class="el-icon-refresh" :class="{ 'vtta__spin': treeLoading }" />
      </button>
      <button
        type="button"
        class="vtta__icon-btn"
        title="重置"
        @click="onResetFilter"
      >
        <i class="el-icon-refresh-left" />
      </button>
    </div>

    <div v-loading="treeLoading" class="vtta__tree-wrap">
      <el-tree
        ref="teamTreeRef"
        class="vtta__tree"
        :data="displayTree"
        node-key="id"
        highlight-current
        :indent="18"
        default-expand-all
        :expand-on-click-node="false"
        :props="{ children: 'children', label: 'label' }"
        :render-content="renderTreeContent"
        @node-click="onTreeNodeClick"
      />
      <div v-if="!treeLoading && !displayTree.length" class="vtta__empty">暂无分组数据</div>
    </div>

    <div v-if="!readonly" class="vtta__actions">
      <button type="button" v-permission="'vehicle:add'" class="vtta__action-btn" title="新增分组" @click="openAddGroupDialog">
        <i class="el-icon-plus" />
      </button>
      <button
        type="button"
        class="vtta__action-btn"
        v-permission="'vehicle:edit'"
        title="编辑分组"
        :disabled="!selectedNode || selectedNode.ungrouped"
        @click="openEditGroupDialog"
      >
        <i class="el-icon-edit" />
      </button>
      <button
        type="button"
        class="vtta__action-btn"
        v-permission="'vehicle:delete'"
        title="删除分组"
        :disabled="!selectedNode || selectedNode.ungrouped"
        @click="onDeleteSelectedGroup"
      >
        <i class="el-icon-minus" />
      </button>
      <button type="button" v-permission="'vehicle:delete'" class="vtta__action-btn" title="批量删除分组" @click="openBatchDeleteDialog">
        <i class="el-icon-delete" />
      </button>
    </div>

    <el-dialog
      v-if="!readonly"
      :title="groupDialogTitle"
      :visible.sync="groupFormVisible"
      width="32rem"
      append-to-body
      :close-on-click-modal="false"
      custom-class="vtta-group-dialog"
      @closed="onGroupDialogClosed"
    >
      <el-form ref="groupFormRef" :model="groupForm" :rules="groupFormRules" label-width="6rem" label-position="right">
        <el-form-item v-if="!groupIsEdit" label="上级分组:" prop="parentPath">
          <el-cascader
            v-model="groupForm.parentPath"
            :options="parentTeamTree"
            :props="teamCascaderProps"
            placeholder="请选择"
            style="width: 100%"
            clearable
            filterable
            :disabled="treeLoading"
            @change="onParentChange"
          />
        </el-form-item>
        <el-form-item label="名称:" prop="teamName">
          <el-input v-model="groupForm.teamName" placeholder="请输入标题" maxlength="100" clearable />
        </el-form-item>
        <el-form-item label="排序:" prop="sort">
          <el-input v-model="groupForm.sort" placeholder="请输入排序" maxlength="10" clearable />
        </el-form-item>
        <el-form-item label="状态:"><el-switch v-model="groupForm.enabled" active-text="启用" inactive-text="停用" /></el-form-item>
      </el-form>
      <span slot="footer" class="vtta-dialog__footer">
        <el-button @click="resetGroupForm"><i class="el-icon-refresh-left" /> 重置</el-button>
        <el-button v-permission="groupForm.id ? 'vehicle:edit' : 'vehicle:add'" type="primary" :loading="groupSubmitLoading" @click="submitGroupForm">
          <i class="el-icon-check" /> 提交
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      v-if="!readonly"
      title="批量删除"
      :visible.sync="batchDeleteVisible"
      width="28rem"
      append-to-body
      :close-on-click-modal="false"
      custom-class="vtta-batch-dialog"
      @closed="onBatchDeleteDialogClosed"
    >
      <div class="vtta-batch__toolbar">
        <el-input
          v-model="batchSearchText"
          placeholder="输入进行查询"
          clearable
          prefix-icon="el-icon-search"
          class="vtta-batch__search"
          @input="syncBatchSearchExpand"
          @clear="syncBatchSearchExpand"
        />
        <button type="button" class="vtta__icon-btn" title="刷新" @click="loadTeamTree">
          <i class="el-icon-refresh" />
        </button>
      </div>
      <div class="vtta-batch__tree-wrap">
        <el-tree
          ref="batchTreeRef"
          class="vtta__tree"
          :data="batchDisplayTree"
          show-checkbox
          node-key="id"
          :indent="18"
          :default-expand-all="!!batchSearchText.trim()"
          :props="{ children: 'children', label: 'label' }"
          :render-content="renderTreeContent"
        />
      </div>
      <span slot="footer" class="vtta-batch__footer">
        <el-button @click="batchDeleteVisible = false">关闭</el-button>
        <el-button v-permission="'vehicle:delete'" type="primary" :loading="batchDeleteLoading" @click="submitBatchDelete">删除</el-button>
      </span>
    </el-dialog>
  </aside>
</template>

<script>
import { authState } from '@/utils/auth'
import { applyElTreeExpandAll, buildGroupLabel, filterGarageTreeByQuery } from '@/utils/garageTree'
import { getVehicleGroups, getVehicleTree, createVehicleGroup, updateVehicleGroup, deleteVehicleGroup, isRequestSuccess } from '@/views/in-transit/lingdongApi'

function emptyGroupForm() {
  return { id: '', parentPath: [], parentId: '0', selectParent_select_input: '', teamName: '', sort: '1', enabled: true }
}

function buildTeamTree(groups, rawTree) {
  const totals = {}
  const direct = {}
  let ungrouped = 0
  for (const node of rawTree || []) {
    if (node.nodeType === 'GROUP') totals[String(node.groupId)] = Number(node.vehicleCount) || 0
    if (node.nodeType === 'UNGROUPED') ungrouped = Number(node.vehicleCount) || 0
    if (node.nodeType === 'VEHICLE' && node.groupId != null) direct[String(node.groupId)] = (direct[String(node.groupId)] || 0) + 1
  }
  const nodes = (groups || []).map(item => {
    const id = String(item.id)
    const counts = { vehicleCount: direct[id] || 0, totalVehicleCount: totals[id] || 0 }
    return { ...item, ...counts, id, parentId: String(item.parentId || 0), teamName: item.groupName, label: buildGroupLabel(item.groupName, counts), sort: item.sortOrder, children: [] }
  })
  const map = {}
  nodes.forEach(node => { map[node.id] = node })
  const roots = []
  nodes.forEach(node => {
    if (node.parentId !== '0' && map[node.parentId]) map[node.parentId].children.push(node)
    else roots.push(node)
  })
  const sort = list => { list.sort((first, second) => Number(first.sort) - Number(second.sort)); list.forEach(node => sort(node.children)) }
  sort(roots)
  roots.unshift({ id: '0', parentId: '0', teamName: '未分组', label: buildGroupLabel('未分组', { vehicleCount: ungrouped }), vehicleCount: ungrouped, status: 1, ungrouped: true, disabled: true, children: [] })
  return roots
}

function availableParents(nodes) {
  return (nodes || []).filter(node => !node.ungrouped).map(node => ({ ...node, disabled: Number(node.status) !== 1, children: availableParents(node.children) }))
}

export default {
  name: 'VehicleTeamTreeAside',
  props: { readonly: { type: Boolean, default: false } },
  data() {
    return {
      treeLoading: false, treeSequence: 0, destroyed: false,
      searchText: '', teamTree: [], selectedNode: null,
      groupFormVisible: false, groupIsEdit: false, groupSubmitLoading: false,
      groupFormSnapshot: null, groupForm: emptyGroupForm(), groupDialogSequence: 0,
      teamCascaderProps: { value: 'id', label: 'teamName', children: 'children', checkStrictly: true, emitPath: true },
      groupFormRules: {
        teamName: [{ required: true, message: '请输入名称', trigger: 'blur' }],
        sort: [{ required: true, message: '请输入排序', trigger: 'blur' }, { pattern: /^\d+$/, message: '排序只能填写非负整数', trigger: 'blur' }]
      },
      batchDeleteVisible: false, batchSearchText: '', batchDeleteLoading: false
    }
  },
  computed: {
    managementAccount() { return authState.user },
    displayTree() { return filterGarageTreeByQuery(this.teamTree, String(this.searchText || '').trim()) },
    batchDisplayTree() { return filterGarageTreeByQuery(this.teamTree, String(this.batchSearchText || '').trim()) },
    parentTeamTree() { return availableParents(this.teamTree) },
    groupDialogTitle() { return this.groupIsEdit ? '修改分组' : '新增分组' },
    searchPlaceholder() { return '输入进行查询' }
  },
  watch: {
    managementAccount() {
      this.treeSequence++
      this.groupDialogSequence++
      this.teamTree = []
      this.selectedNode = null
      this.groupFormVisible = false
      this.batchDeleteVisible = false
      this.groupFormSnapshot = null
      this.groupForm = emptyGroupForm()
      this.treeLoading = false
      this.groupSubmitLoading = false
      this.batchDeleteLoading = false
      if (authState.user) this.loadTeamTree()
    },
    displayTree() { this.syncSearchExpand() },
    batchDisplayTree() { this.syncBatchSearchExpand() }
  },
  mounted() { this.loadTeamTree() },
  beforeDestroy() { this.destroyed = true; this.treeSequence++ },
  methods: {
    getTeamTree() { return this.teamTree },
    async loadTeamTree() {
      const sequence = ++this.treeSequence
      const actor = authState.user
      const current = () => !this.destroyed && sequence === this.treeSequence && actor === authState.user
      this.treeLoading = true
      try {
        const [groups, tree] = await Promise.all([getVehicleGroups(), getVehicleTree()])
        if (!current()) return
        if (!isRequestSuccess(groups) || !isRequestSuccess(tree)) throw new Error((groups && groups.msg) || (tree && tree.msg) || '车辆分组加载失败')
        this.teamTree = buildTeamTree(groups.data || [], tree.data || [])
        if (this.selectedNode) this.selectedNode = this.findTeamNode(this.selectedNode.id)
        this.$emit('tree-change', this.teamTree)
        this.onSearchInput()
      } catch (error) {
        if (!current()) return
        this.teamTree = []
        this.selectedNode = null
        this.$emit('tree-change', [])
        this.$message.error(error.msg || error.message || '车辆分组加载失败')
      } finally { if (current()) this.treeLoading = false }
    },
    onSearchInput() { this.syncSearchExpand() },
    syncSearchExpand() { this.$nextTick(() => applyElTreeExpandAll(this.$refs.teamTreeRef, true)) },
    syncBatchSearchExpand() { this.$nextTick(() => applyElTreeExpandAll(this.$refs.batchTreeRef, !!this.batchSearchText.trim())) },
    buildGroupCountLabel(data) {
      if (!data) return ''
      if (data.vehicleCount != null && data.totalVehicleCount != null) return `${data.vehicleCount}/${data.totalVehicleCount}`
      return data.totalVehicleCount != null ? String(data.totalVehicleCount) : data.vehicleCount != null ? String(data.vehicleCount) : ''
    },
    renderTreeContent(h, { node, data }) {
      const children = [h('i', { class: 'vtta-tree__icon vtta-tree__icon--folder el-icon-folder', attrs: { 'aria-hidden': 'true' } }), h('span', { class: 'vtta-tree__group-name' }, data.teamName || data.label || node.label || '')]
      const count = this.buildGroupCountLabel(data)
      if (count) children.push(h('span', { class: 'vtta-tree__group-count' }, ` [${count}]`))
      return h('span', { class: 'vtta-tree__node' }, children)
    },
    findTeamNode(id, nodes = this.teamTree) {
      for (const node of nodes || []) {
        if (String(node.id) === String(id)) return node
        const found = this.findTeamNode(id, node.children)
        if (found) return found
      }
      return null
    },
    buildTeamPathToNode(id, nodes = this.teamTree, path = []) {
      for (const node of nodes || []) {
        const next = [...path, node.id]
        if (String(node.id) === String(id)) return next
        const found = this.buildTeamPathToNode(id, node.children, next)
        if (found.length) return found
      }
      return []
    },
    clearSelection() {
      this.selectedNode = null
      const tree = this.$refs.teamTreeRef
      if (tree) tree.setCurrentKey(null)
    },
    onResetFilter() { this.searchText = ''; this.clearSelection(); this.syncSearchExpand(); this.$emit('team-clear') },
    onTreeNodeClick(data) {
      if (!data || data.id == null) return
      this.selectedNode = data
      this.$emit('team-select', { teamId: String(data.id), teamName: data.teamName || data.label || '', teamPath: this.buildTeamPathToNode(data.id), ungrouped: !!data.ungrouped })
    },
    resolveGroupParentFromPath(path) {
      const values = Array.isArray(path) ? path : []
      const parentId = values.length ? String(values[values.length - 1]) : '0'
      const node = parentId === '0' ? null : this.findTeamNode(parentId)
      return { parentId, selectParent_select_input: node ? node.teamName : '' }
    },
    onParentChange(path) { Object.assign(this.groupForm, this.resolveGroupParentFromPath(path)) },
    openAddGroupDialog() {
      this.groupDialogSequence++
      this.groupSubmitLoading = false
      this.groupIsEdit = false
      const form = emptyGroupForm()
      if (this.selectedNode && !this.selectedNode.ungrouped && Number(this.selectedNode.status) === 1) {
        form.parentPath = this.buildTeamPathToNode(this.selectedNode.id)
        Object.assign(form, this.resolveGroupParentFromPath(form.parentPath))
      }
      this.groupForm = form
      this.groupFormSnapshot = { ...form, parentPath: [...form.parentPath] }
      this.groupFormVisible = true
      this.$nextTick(() => this.$refs.groupFormRef && this.$refs.groupFormRef.clearValidate())
    },
    openEditGroupDialog() {
      if (!this.selectedNode || this.selectedNode.ungrouped) return this.$message.warning('请先选择分组')
      this.groupDialogSequence++
      this.groupSubmitLoading = false
      const node = this.selectedNode
      this.groupIsEdit = true
      this.groupForm = { id: String(node.id), parentPath: [], parentId: String(node.parentId || 0), selectParent_select_input: '', teamName: node.teamName || '', sort: String(node.sort == null ? 1 : node.sort), enabled: Number(node.status) === 1 }
      this.groupFormSnapshot = { ...this.groupForm, parentPath: [] }
      this.groupFormVisible = true
      this.$nextTick(() => this.$refs.groupFormRef && this.$refs.groupFormRef.clearValidate())
    },
    resetGroupForm() {
      if (this.groupFormSnapshot) this.groupForm = { ...this.groupFormSnapshot, parentPath: [...this.groupFormSnapshot.parentPath] }
      this.$nextTick(() => this.$refs.groupFormRef && this.$refs.groupFormRef.clearValidate())
    },
    onGroupDialogClosed() {
      if (this.groupFormVisible) return
      this.groupDialogSequence++
      this.groupSubmitLoading = false
      this.groupFormSnapshot = null
      this.groupForm = emptyGroupForm()
    },
    buildGroupPayload() {
      // 源编辑隐藏上级，本系统更新接口仍需要原上级，不能默认为根节点。
      const parentId = this.groupIsEdit ? this.groupFormSnapshot.parentId : this.resolveGroupParentFromPath(this.groupForm.parentPath).parentId
      return { parentId, groupName: this.groupForm.teamName, sortOrder: Number(this.groupForm.sort), status: this.groupForm.enabled ? 1 : 0 }
    },
    submitGroupForm() {
      if (this.groupSubmitLoading) return
      return this.$refs.groupFormRef.validate(async valid => {
        if (!valid) return
        if (!/^\d+$/.test(String(this.groupForm.sort)) || Number(this.groupForm.sort) > 2147483647) return this.$message.warning('排序应为0至2147483647的整数')
        const actor = authState.user
        const dialogSequence = this.groupDialogSequence
        const selectedId = this.selectedNode && this.selectedNode.id
        this.groupSubmitLoading = true
        try {
          const payload = this.buildGroupPayload()
          const res = this.groupIsEdit ? await updateVehicleGroup(this.groupForm.id, payload) : await createVehicleGroup(payload)
          if (this.destroyed || actor !== authState.user) return
          if (!isRequestSuccess(res)) throw new Error(res.msg || '分组保存失败')
          if (dialogSequence === this.groupDialogSequence) {
            this.$message.success('操作成功')
            this.groupFormVisible = false
          }
          await this.loadTeamTree()
          if (this.destroyed || actor !== authState.user) return
          const selected = dialogSequence === this.groupDialogSequence && selectedId && this.findTeamNode(selectedId)
          if (selected) {
            this.onTreeNodeClick(selected)
            this.$nextTick(() => this.$refs.teamTreeRef && this.$refs.teamTreeRef.setCurrentKey(selectedId))
          }
          this.$emit('changed')
        } catch (error) { if (!this.destroyed && actor === authState.user && dialogSequence === this.groupDialogSequence) this.$message.error(error.msg || error.message || '分组保存失败') }
        finally { if (!this.destroyed && actor === authState.user && dialogSequence === this.groupDialogSequence) this.groupSubmitLoading = false }
      })
    },
    async onDeleteSelectedGroup() {
      if (!this.selectedNode || this.selectedNode.ungrouped) return this.$message.warning('请先选择分组')
      const node = this.selectedNode
      const actor = authState.user
      try {
        await this.$confirm(`确认删除"${node.teamName || node.label}"吗？`, '提示', { type: 'warning' })
        if (this.destroyed || actor !== authState.user) return
        const res = await deleteVehicleGroup(node.id)
        if (this.destroyed || actor !== authState.user) return
        if (!isRequestSuccess(res)) throw new Error(res.msg || '删除失败')
        this.$message.success('删除成功')
        this.clearSelection()
        this.$emit('team-clear')
        await this.loadTeamTree()
        this.$emit('changed')
      } catch (error) { if (error && error !== 'cancel' && error !== 'close' && !this.destroyed && actor === authState.user) this.$message.error(error.msg || error.message || '删除失败') }
    },
    openBatchDeleteDialog() {
      this.batchSearchText = ''
      this.batchDeleteVisible = true
      this.$nextTick(() => { if (this.$refs.batchTreeRef) this.$refs.batchTreeRef.setCheckedKeys([]); this.syncBatchSearchExpand() })
    },
    onBatchDeleteDialogClosed() { this.batchSearchText = '' },
    async submitBatchDelete() {
      if (this.batchDeleteLoading) return
      const tree = this.$refs.batchTreeRef
      if (!tree) return
      const ids = [...new Set(tree.getCheckedKeys().map(String).filter(id => id !== '0'))]
      if (!ids.length) return this.$message.warning('请先选择要删除的分组')
      // 本系统禁止删除仍有子组的父组，勾选父子时必须先删叶子。
      ids.sort((first, second) => this.buildTeamPathToNode(second).length - this.buildTeamPathToNode(first).length)
      const actor = authState.user
      try {
        await this.$confirm(`确认删除选中的 ${ids.length} 个分组吗？`, '提示', { type: 'warning' })
        if (this.destroyed || actor !== authState.user) return
        this.batchDeleteLoading = true
        try {
          for (const id of ids) {
            if (this.destroyed || actor !== authState.user) return
            const res = await deleteVehicleGroup(id)
            if (this.destroyed || actor !== authState.user) return
            if (!isRequestSuccess(res)) throw new Error(`${(this.findTeamNode(id) || {}).teamName || id}：${res.msg || '删除失败'}`)
          }
          this.$message.success('删除成功')
          this.batchDeleteVisible = false
        } finally {
          if (!this.destroyed && actor === authState.user) {
            this.clearSelection()
            this.$emit('team-clear')
            await this.loadTeamTree()
            this.$emit('changed')
          }
        }
      } catch (error) { if (error && error !== 'cancel' && error !== 'close' && !this.destroyed && actor === authState.user) this.$message.error(error.msg || error.message || '删除失败') }
      finally { if (!this.destroyed && actor === authState.user) this.batchDeleteLoading = false }
    }
  }
}
</script>

<style scoped>
.vtta {
  flex: 0 0 16rem;
  width: 16rem;
  background: #fff;
  border: 1px solid #e6e6e6;
  border-radius: 2px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 8rem);
}

.vtta__toolbar {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem;
  border-bottom: 1px solid #ebeef5;
  flex-shrink: 0;
}

.vtta__search {
  flex: 1;
  min-width: 0;
}

.vtta__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: none;
  border-radius: 0.25rem;
  background: transparent;
  color: var(--layout-primary, #087dff);
  cursor: pointer;
  flex-shrink: 0;
}

.vtta__icon-btn:hover:not(:disabled) {
  background: var(--layout-primary-soft, rgba(8, 125, 255, 0.08));
}

.vtta__icon-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vtta__spin {
  animation: vtta-spin 0.8s linear infinite;
}

@keyframes vtta-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.vtta__tree-wrap {
  flex: 1;
  min-height: 0;
  padding: 0.375rem 0.5rem;
  overflow: auto;
}

.vtta__empty {
  padding: 1.5rem 0;
  text-align: center;
  color: #909399;
  font-size: 0.8125rem;
}

.vtta__tree {
  font-size: 0.875rem;
  background: transparent;
}

.vtta__tree >>> .el-tree-node__content {
  height: 1.75rem;
  line-height: 1.75rem;
  padding-right: 0.25rem;
}

.vtta__tree >>> .el-tree-node.is-current > .el-tree-node__content {
  background-color: var(--layout-primary-soft, rgba(8, 125, 255, 0.08)) !important;
}

.vtta__tree >>> .el-tree-node__content:hover {
  background-color: #f5f7fa;
}

.vtta__tree >>> .el-tree-node__expand-icon {
  color: var(--layout-primary, #087dff);
  font-size: 0.8125rem;
  margin-right: 0.125rem;
}

.vtta__tree >>> .el-tree-node__expand-icon.is-leaf {
  color: transparent;
  pointer-events: none;
}

.vtta__tree >>> .el-tree-node__children {
  position: relative;
}

.vtta__tree >>> .el-tree-node__children::before {
  content: '';
  position: absolute;
  left: 0.4375rem;
  top: 0;
  bottom: 0.5rem;
  border-left: 1px dotted #c0c4cc;
  pointer-events: none;
}

.vtta__tree >>> .el-checkbox {
  margin-right: 0.375rem;
}

.vtta__tree >>> .el-checkbox__inner:hover,
.vtta__tree >>> .el-checkbox__input.is-focus .el-checkbox__inner {
  border-color: var(--layout-primary, #087dff);
}

.vtta__tree >>> .el-checkbox__input.is-checked .el-checkbox__inner,
.vtta__tree >>> .el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: var(--layout-primary, #087dff);
  border-color: var(--layout-primary, #087dff);
}

.vtta__tree >>> .el-checkbox__input.is-checked .el-checkbox__inner:hover,
.vtta__tree >>> .el-checkbox__input.is-indeterminate .el-checkbox__inner:hover {
  background-color: var(--layout-primary-hover, #0670e6);
  border-color: var(--layout-primary-hover, #0670e6);
}

.vtta__tree >>> .vtta-tree__node {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: calc(100% - 0.5rem);
  overflow: hidden;
  vertical-align: middle;
}

.vtta__tree >>> .vtta-tree__icon--folder {
  color: #e6a23c;
  font-size: 1rem;
  flex-shrink: 0;
}

.vtta__tree >>> .vtta-tree__group-name {
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vtta__tree >>> .vtta-tree__group-count {
  color: #909399;
  flex-shrink: 0;
}

.vtta__tree >>> .vtta-tree__vehicle-icon {
  width: 1.125rem;
  height: 1.125rem;
  min-width: 1.125rem;
  flex-shrink: 0;
  object-fit: contain;
  vertical-align: middle;
}

.vtta__tree >>> .vtta-tree__plate {
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vtta__actions {
  display: flex;
  align-items: stretch;
  gap: 0.25rem;
  padding: 0.5rem;
  border-top: 1px solid #ebeef5;
  flex-shrink: 0;
}

.vtta__action-btn {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  height: 2rem;
  padding: 0;
  border: none;
  border-radius: 0.125rem;
  background: var(--layout-primary, #087dff);
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}

.vtta__action-btn:hover:not(:disabled) {
  background: var(--layout-primary-hover, #0670e6);
}

.vtta__action-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.vtta-batch__toolbar {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
}

.vtta-batch__search {
  flex: 1;
  min-width: 0;
}

.vtta-batch__tree-wrap {
  min-height: 16rem;
  max-height: 24rem;
  overflow: auto;
  border: 1px solid #ebeef5;
  border-radius: 0.25rem;
  padding: 0.375rem 0.25rem;
}

.vtta-dialog__footer,
.vtta-batch__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}
</style>

<style>
.vtta-group-dialog .el-dialog__header,
.vtta-batch-dialog .el-dialog__header {
  padding: 0.75rem 1rem;
  background: linear-gradient(
    180deg,
    var(--layout-primary-light, #4da3ff) 0%,
    var(--layout-primary, #087dff) 100%
  );
}

.vtta-group-dialog .el-dialog__title,
.vtta-batch-dialog .el-dialog__title {
  color: #fff;
  font-size: 0.9375rem;
  font-weight: 600;
}

.vtta-group-dialog .el-dialog__headerbtn .el-dialog__close,
.vtta-batch-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #fff;
}

.vtta-group-dialog .el-dialog__body,
.vtta-batch-dialog .el-dialog__body {
  padding: 1rem 1.25rem 0.5rem;
}

.vtta-group-dialog .el-dialog__footer,
.vtta-batch-dialog .el-dialog__footer {
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid #eee;
}
</style>
